import { openableUrl } from './url'

/** Social and listing platforms a client account can include. */
export type PlatformAccess = 'meta_partner' | 'login' | 'invite'

/**
 * What the handle box holds. 'username' platforms show a fixed prefix (the @ or the domain) and store
 * only the name; the profile URL is built from it, and `extract` pulls the name (group 1) out of a pasted URL. 'link' holds a pasted URL.
 */
export type HandleFormat =
  | { kind: 'username'; prefix: string; profileUrl: (name: string) => string; extract: RegExp; pattern: RegExp }
  | { kind: 'link' }

export type Platform = {
  key: PlatformKey
  name: string
  access: PlatformAccess
  /** Whether Sonder typically manages posting here (drives credential requests). */
  defaultManaged: boolean
  defaultIncluded: boolean
  /** Label for the handle box. */
  handleLabel: string
  handlePlaceholder: string
  /** How to find the link, shown under the box label. */
  handleHint?: string
  handleFormat: HandleFormat
  /** Shown as the box's error when it holds something that isn't a usable name or link. */
  handleError?: string
  /** A search that gets someone to the page so they can copy its link. */
  find?: { label: (brand: string) => string; url: (brand: string) => string }
  /** Whether a managed account asks for the login and password. */
  credentials: boolean
  /** Shown to the client when the platform is managed. */
  accessInstructions: string
}

export const PLATFORM_KEYS = ['instagram', 'facebook', 'tiktok', 'google', 'yelp', 'linktree', 'opentable', 'resy'] as const
export type PlatformKey = (typeof PLATFORM_KEYS)[number]

export const PLATFORMS: Record<PlatformKey, Platform> = {
  instagram: {
    key: 'instagram', name: 'Instagram', access: 'meta_partner', defaultManaged: true, defaultIncluded: true,
    handleLabel: 'Handle', handlePlaceholder: 'yourhandle', credentials: true,
    handleFormat: { kind: 'username', prefix: '@', profileUrl: (n) => `https://www.instagram.com/${n}/`, extract: /^(?:https?:\/\/)?(?:www\.|m\.)?instagram\.com\/([^/?#\s]+)/i, pattern: /^[A-Za-z0-9._]+$/ },
    accessInstructions: 'Grant partner access through Meta Business Suite, and share the account login below.',
  },
  facebook: {
    key: 'facebook', name: 'Facebook', access: 'meta_partner', defaultManaged: true, defaultIncluded: true,
    handleLabel: 'Page', handlePlaceholder: 'yourpage', credentials: false,
    handleFormat: { kind: 'username', prefix: 'facebook.com/', profileUrl: (n) => `https://www.facebook.com/${n}`, extract: /^(?:https?:\/\/)?(?:www\.|m\.|web\.)?(?:facebook|fb)\.com\/(profile\.php\?id=\d+|[^/?#\s]+)/i, pattern: /^(?:[A-Za-z0-9.-]+|profile\.php\?id=\d+)$/ },
    accessInstructions: 'Grant partner access to the Page through Meta Business Suite.',
  },
  tiktok: {
    key: 'tiktok', name: 'TikTok', access: 'login', defaultManaged: true, defaultIncluded: true,
    handleLabel: 'Handle', handlePlaceholder: 'yourhandle', credentials: true,
    handleFormat: { kind: 'username', prefix: '@', profileUrl: (n) => `https://www.tiktok.com/@${n}`, extract: /^(?:https?:\/\/)?(?:www\.|m\.)?tiktok\.com\/@?([^/?#\s]+)/i, pattern: /^[A-Za-z0-9._]+$/ },
    accessInstructions: 'TikTok has no agency access; share the login and we will set up a passkey on our end.',
  },
  google: {
    key: 'google', name: 'Google Business Profile', access: 'invite', defaultManaged: false, defaultIncluded: true,
    handleLabel: 'Profile share link', handlePlaceholder: 'AbC123xyz', credentials: false,
    handleHint: 'Search the business on Google, tap Share on its profile, then Copy link and paste it here.',
    handleFormat: { kind: 'username', prefix: 'share.google/', profileUrl: (n) => `https://share.google/${n}`, extract: /^(?:https?:\/\/)?share\.google\/([^/?#\s]+)/i, pattern: /^[A-Za-z0-9_-]+$/ },
    handleError: 'Paste the link from Share on the business’s Google profile. It starts with share.google/.',
    find: { label: (b) => `Find ${b} on Google`, url: (b) => `https://www.google.com/search?q=${encodeURIComponent(b)}` },
    accessInstructions: 'Add hello@sondermediahouse.com as a Manager on the Business Profile.',
  },
  yelp: {
    key: 'yelp', name: 'Yelp', access: 'invite', defaultManaged: false, defaultIncluded: true,
    handleLabel: 'Page', handlePlaceholder: 'your-business-city', credentials: false,
    handleFormat: { kind: 'username', prefix: 'yelp.com/biz/', profileUrl: (n) => `https://www.yelp.com/biz/${n}`, extract: /^(?:https?:\/\/)?(?:www\.|m\.)?yelp\.com\/biz\/([^/?#\s]+)/i, pattern: /^[A-Za-z0-9%._-]+$/ },
    accessInstructions: 'Add hello@sondermediahouse.com as a user in Yelp for Business.',
  },
  linktree: {
    key: 'linktree', name: 'Linktree', access: 'login', defaultManaged: false, defaultIncluded: true,
    handleLabel: 'Username', handlePlaceholder: 'yourname', credentials: true,
    handleFormat: { kind: 'username', prefix: 'linktr.ee/', profileUrl: (n) => `https://linktr.ee/${n}`, extract: /^(?:https?:\/\/)?(?:www\.)?linktr\.ee\/([^/?#\s]+)/i, pattern: /^[A-Za-z0-9._-]+$/ },
    accessInstructions: 'Share the login, or invite hello@sondermediahouse.com as an admin if your plan allows.',
  },
  opentable: {
    key: 'opentable', name: 'OpenTable', access: 'login', defaultManaged: false, defaultIncluded: true,
    handleLabel: 'Page', handlePlaceholder: 'your-restaurant-city', credentials: true,
    handleFormat: { kind: 'username', prefix: 'opentable.com/r/', profileUrl: (n) => `https://www.opentable.com/r/${n}`, extract: /^(?:https?:\/\/)?(?:www\.|m\.)?opentable\.com\/r\/([^/?#\s]+)/i, pattern: /^[A-Za-z0-9%._-]+$/ },
    accessInstructions: 'Reference only unless we manage your listing photos.',
  },
  resy: {
    key: 'resy', name: 'Resy', access: 'login', defaultManaged: false, defaultIncluded: false,
    handleLabel: 'Link', handlePlaceholder: 'resy.com/cities/…', credentials: true,
    handleFormat: { kind: 'link' },
    accessInstructions: 'Reference only unless we manage your listing photos.',
  },
}

/**
 * Tidies what someone types or pastes into a username box: drops the leading @ (the box shows its own
 * prefix) and reduces a pasted profile URL to the name. Leaves anything else alone, so a staff note
 * like "Not found; check with Stephanie" survives an edit.
 */
export function cleanHandleInput(platform: PlatformKey, raw: string): string {
  const f = PLATFORMS[platform].handleFormat
  if (f.kind !== 'username') return raw
  const v = raw.replace(/^\s+/, '')
  return (f.extract.exec(v)?.[1] ?? v).replace(/^@+/, '')
}

/** The public page for whatever is in the handle box, or null when there is nothing openable yet. */
export function platformUrl(platform: PlatformKey, value: string): string | null {
  const f = PLATFORMS[platform].handleFormat
  if (f.kind === 'link') return openableUrl(value)
  const name = cleanHandleInput(platform, value).trim()
  return f.pattern.test(name) ? f.profileUrl(name) : null
}

export const ACCESS_STATUSES = [
  { value: 'not_started', label: 'Not started' },
  { value: 'granted', label: 'Access granted from my side' },
  { value: 'connected', label: 'Connected' },
  { value: 'no_account', label: 'No account yet' },
] as const
export type AccessStatus = (typeof ACCESS_STATUSES)[number]['value']

export const ACCESS_STATUS_LABEL: Record<AccessStatus, string> = Object.fromEntries(
  ACCESS_STATUSES.map((s) => [s.value, s.label]),
) as Record<AccessStatus, string>
